/**
 * SEO Copy Generation Engine for Sampaoli Product Pages.
 *
 * Generates unique, keyword-rich descriptions dynamically from Shopify
 * product data (title, tags, productType, options, variants).
 *
 * Priority: if Shopify has a rich body_html description the frontend shows
 * it as-is in the "Description" H2. The other H2 sections (Materials,
 * Inspiration, Shipping) are always generated here.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProductData {
  title: string;
  description: string;          // plain-text description from Storefront API
  handle: string;
  tags: string[];
  productType: string;
  vendor: string;
  options: Array<{ name: string; values: string[] }>;
  variants: {
    edges: Array<{
      node: {
        sku?: string;
        price: { amount: string; currencyCode: string };
        availableForSale: boolean;
        selectedOptions: Array<{ name: string; value: string }>;
      };
    }>;
  };
  images: { edges: Array<{ node: { url: string; altText: string | null } }> };
}

type Collection = 'mountain' | 'fauna' | 'equestrian' | 'unknown';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function detectCollection(tags: string[]): Collection {
  const lower = tags.map(t => t.toLowerCase());
  if (lower.includes('montagna')) return 'mountain';
  if (lower.includes('fauna')) return 'fauna';
  if (lower.includes('equitazione')) return 'equestrian';
  return 'unknown';
}

function detectProductCategory(type: string, tags: string[]): string {
  const t = type.toLowerCase();
  const lower = tags.map(tg => tg.toLowerCase());
  if (t.includes('braccialett') || lower.includes('bracciale')) return 'bracelet';
  if (t.includes('collana') || lower.includes('collane')) return 'necklace';
  if (t.includes('cintura') || lower.includes('cinture')) return 'belt';
  if (lower.includes('spilla') || lower.includes('spille')) return 'pin';
  if (lower.includes('portachiavi')) return 'keychain';
  if (lower.includes('tappo') || lower.includes('tappi')) return 'cork';
  if (lower.includes('orecchini')) return 'earrings';
  return 'accessory';
}

/** Simple deterministic hash from a string to pick template variants. */
function simpleHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ---------------------------------------------------------------------------
// English product type labels
// ---------------------------------------------------------------------------

const categoryLabelsEN: Record<string, string> = {
  bracelet: 'bracelet',
  necklace: 'necklace',
  belt: 'belt',
  pin: 'pin',
  keychain: 'keychain',
  cork: 'decorative cork stopper',
  earrings: 'earrings',
  accessory: 'accessory',
};

const categoryLabelsIT: Record<string, string> = {
  bracelet: 'braccialetto',
  necklace: 'collana',
  belt: 'cintura',
  pin: 'spilla',
  keychain: 'portachiavi',
  cork: 'tappo decorativo',
  earrings: 'orecchini',
  accessory: 'accessorio',
};

// ---------------------------------------------------------------------------
// Materials extraction
// ---------------------------------------------------------------------------

export function extractMaterials(description: string, tags: string[], options: ProductData['options']): string[] {
  const mats: string[] = [];
  const descLower = description.toLowerCase();

  // Bronze is default for this brand
  if (descLower.includes('bronzo') || descLower.includes('bronze') || !description) {
    mats.push('bronze');
  }
  // Only detect plating when the description explicitly mentions plating terms
  const silverPlatingPatterns = ['placcato argento', 'bagno d\'argento', 'bagno argento', 'silver plated', 'silver plating', 'argentatura'];
  if (silverPlatingPatterns.some(p => descLower.includes(p))) mats.push('silver plating');
  const goldPlatingPatterns = ['placcato oro', 'bagno d\'oro', 'bagno oro', 'gold plated', 'gold plating', 'doratura', 'dorato'];
  if (goldPlatingPatterns.some(p => descLower.includes(p))) mats.push('gold plating');
  const rhodiumPatterns = ['rodio', 'rhodium', 'rodiatura', 'rodiato'];
  if (rhodiumPatterns.some(p => descLower.includes(p))) mats.push('rhodium plating');
  if (descLower.includes('pelle') || descLower.includes('cuoio') || descLower.includes('leather')) mats.push('Italian leather');
  if (descLower.includes('poliammide') || descLower.includes('cordino') || descLower.includes('cordini')) mats.push('polyamide cord');
  if (descLower.includes('smalto') || descLower.includes('enamel')) mats.push('colored enamel');
  if (descLower.includes('sughero') || descLower.includes('cork')) mats.push('synthetic cork');

  // Check options for leather colors, cord colors, etc.
  options.forEach(opt => {
    const nm = opt.name.toLowerCase();
    if ((nm.includes('cuoio') || nm.includes('pelle')) && !mats.includes('Italian leather')) {
      mats.push('Italian leather');
    }
    if (nm.includes('cordino') && !mats.includes('polyamide cord')) {
      mats.push('polyamide cord');
    }
  });

  if (mats.length === 0) mats.push('bronze');
  return [...new Set(mats)];
}

export function extractFinishes(options: ProductData['options']): string[] {
  const finishes: string[] = [];
  options.forEach(opt => {
    const nm = opt.name.toLowerCase();
    if (nm.includes('color') || nm.includes('colore') || nm.includes('finitura') || nm.includes('finish')) {
      finishes.push(...opt.values);
    }
  });
  return finishes;
}

export function extractSizes(options: ProductData['options']): string[] {
  const sizes: string[] = [];
  options.forEach(opt => {
    const nm = opt.name.toLowerCase();
    if (nm.includes('lunghezza') || nm.includes('taglia') || nm.includes('size') || nm.includes('misura')) {
      sizes.push(...opt.values.map(v => {
        if (/^\d+$/.test(v)) return `${v} cm`;
        return v;
      }));
    }
  });
  return sizes;
}

// ---------------------------------------------------------------------------
// Dynamic Description Generation (EN)
// ---------------------------------------------------------------------------

const mountainIntros = [
  (title: string, cat: string) =>
    `The ${title} is a handcrafted bronze ${cat} that captures the spirit of high-altitude mountaineering. Designed for climbers, alpinists, and mountain enthusiasts, this piece transforms essential climbing gear into a wearable work of art.`,
  (title: string, cat: string) =>
    `Inspired by the rugged beauty of the Italian Dolomites, the ${title} is a bronze ${cat} meticulously crafted by artisan Andrea Sampaoli. Every detail reflects the precision and passion of real mountain equipment.`,
  (title: string, cat: string) =>
    `For those who find freedom on vertical walls and icy ridges, the ${title} bronze ${cat} is a miniature tribute to the mountains. Each element is modeled after authentic climbing tools, rendered in lasting bronze.`,
  (title: string, cat: string) =>
    `The ${title} is a bronze ${cat} born from the peaks of the Italian Alps. Artisan Andrea Sampaoli recreates the tools of alpinism in exquisite miniature, forging each piece by hand in his Euganean Hills workshop.`,
  (title: string, cat: string) =>
    `Designed for those who live and breathe the mountains, the ${title} bronze ${cat} is a wearable celebration of climbing culture. Every ridge, knurl, and curve is faithfully reproduced from real mountaineering equipment.`,
  (title: string, cat: string) =>
    `Carry the summit with you. The ${title} is a handcrafted bronze ${cat} that transforms iconic climbing gear into an elegant everyday accessory, crafted with the same precision a climber trusts on the rock face.`,
  (title: string, cat: string) =>
    `From carabiner gates to ice axe picks, the ${title} bronze ${cat} captures the engineering beauty of mountain equipment. Hand-cast and hand-finished in Teolo, Italy, it's built to endure like the mountains themselves.`,
  (title: string, cat: string) =>
    `The ${title} is more than a ${cat} — it's a handcrafted bronze tribute to the vertical world. Inspired by the gear that keeps climbers safe, this piece brings alpine adventure into daily life.`,
];

const faunaIntros = [
  (title: string, cat: string) =>
    `The ${title} is a handcrafted bronze ${cat} celebrating the wild beauty of alpine fauna. Every anatomical detail—antlers, fur texture, feather patterns—is faithfully reproduced by artisan hands in the Euganean Hills.`,
  (title: string, cat: string) =>
    `A tribute to the untamed wildlife of the Italian Alps, the ${title} bronze ${cat} captures the essence of nature's most majestic creatures. Ideal for hunters, naturalists, and wildlife enthusiasts who carry their passion everywhere.`,
  (title: string, cat: string) =>
    `Designed for lovers of nature and the hunting tradition, the ${title} is a bronze wildlife ${cat} that brings the spirit of the forest and the mountains into daily life. Each piece is individually cast and finished by hand.`,
  (title: string, cat: string) =>
    `The ${title} bronze ${cat} is a miniature masterpiece of wildlife art. Every muscle, feather, and antler tine is sculpted from life, capturing the grace and power of alpine animals in enduring bronze.`,
  (title: string, cat: string) =>
    `For those who cherish the call of the wild, the ${title} is a handcrafted bronze ${cat} that preserves the beauty of Europe's most iconic wildlife species. Made by hand in Andrea Sampaoli's Italian workshop.`,
  (title: string, cat: string) =>
    `Nature's finest creatures, immortalized in bronze. The ${title} ${cat} captures the spirit of alpine fauna with anatomical precision and artisan passion, making it a treasured piece for any wildlife enthusiast.`,
  (title: string, cat: string) =>
    `The ${title} is a bronze ${cat} that tells the story of Europe's wild heritage. From forest floors to mountain ridges, each detail is hand-cast to honor the animals that define the alpine landscape.`,
  (title: string, cat: string) =>
    `Crafted for hunters and nature lovers, the ${title} bronze ${cat} is a wearable work of wildlife art. Each piece begins as a hand-sculpted model, ensuring every anatomical detail is true to life.`,
];

const equestrianIntros = [
  (title: string, cat: string) =>
    `The ${title} is a handcrafted bronze ${cat} inspired by the noble world of horsemanship. From English riding traditions to Western spirit, this piece celebrates the timeless bond between rider and horse.`,
  (title: string, cat: string) =>
    `Crafted for equestrian enthusiasts and horse lovers, the ${title} bronze ${cat} faithfully reproduces iconic riding equipment in miniature. Every buckle, curve, and texture is hand-finished in Andrea Sampaoli's Italian workshop.`,
  (title: string, cat: string) =>
    `A symbol of elegance and equestrian passion, the ${title} is a bronze ${cat} that brings the beauty of the stable and the arena into wearable art. Made in Italy with traditional casting techniques.`,
  (title: string, cat: string) =>
    `The ${title} bronze ${cat} captures the refined world of horsemanship in miniature. From snaffle bits to stirrup irons, every curve and proportion is modeled on authentic equestrian equipment.`,
  (title: string, cat: string) =>
    `For riders, trainers, and horse lovers everywhere, the ${title} is a handcrafted bronze ${cat} that embodies the spirit of the stable. Each piece is individually cast and polished to a warm patina.`,
  (title: string, cat: string) =>
    `Saddle up in style. The ${title} bronze ${cat} transforms the tools of horsemanship into elegant wearable art, hand-forged in the Italian tradition by artisan Andrea Sampaoli.`,
  (title: string, cat: string) =>
    `The ${title} is a bronze ${cat} that bridges the worlds of English dressage and Western riding. Every detail — from leather stitching to metal hardware — is faithfully reproduced in handcrafted bronze.`,
  (title: string, cat: string) =>
    `Born from a love of horses and Italian craftsmanship, the ${title} bronze ${cat} is a tribute to the equestrian lifestyle. Each piece carries the warmth and character of hand-finished bronze.`,
];

const genericIntros = [
  (title: string, cat: string) =>
    `The ${title} is a handcrafted bronze ${cat} created by artisan Andrea Sampaoli in Teolo, Italy. Combining traditional Italian metalworking with contemporary design, each piece tells a story of craftsmanship and passion.`,
  (title: string, cat: string) =>
    `Designed and crafted in the heart of the Euganean Hills, the ${title} bronze ${cat} showcases the artistry of traditional Italian bronze casting. A unique accessory for those who appreciate authentic handmade quality.`,
];

function getDescriptionIntro(title: string, collection: Collection, category: string, handle: string): string {
  const cat = categoryLabelsEN[category] || 'accessory';
  const hash = simpleHash(handle);

  const pool =
    collection === 'mountain' ? mountainIntros :
    collection === 'fauna' ? faunaIntros :
    collection === 'equestrian' ? equestrianIntros :
    genericIntros;

  return pool[hash % pool.length](title, cat);
}

// ---------------------------------------------------------------------------
// Inspiration Section
// ---------------------------------------------------------------------------

const mountainInspirations = [
  'Born from the climbing culture of the Dolomites and the Western Alps, this piece evokes the thrill of reaching a summit at dawn. The bronze material echoes the warm tones of mountain rock under golden sunlight—a perfect companion for anyone whose heart belongs to the peaks.',
  'Andrea Sampaoli created his first mountain-themed piece as a Christmas gift for his climbing partners in 2003. That spirit of camaraderie and shared adventure lives on in every detail of this design, connecting wearers to a community of mountain lovers worldwide.',
  'The mountains teach patience, precision, and respect for nature. This piece embodies those values through its meticulous craftsmanship—each curve and surface is refined by hand, mirroring the careful attention a climber gives to every hold and anchor.',
  'From the first light on a Dolomite ridge to the quiet triumph of a summit reached, this piece distills the emotional landscape of the mountains into tangible bronze. It is an heirloom for those who measure life in vertical meters.',
  'Every mountaineer knows the feeling of trusting their gear with their life. This piece honors that trust by recreating climbing equipment with the same precision and integrity—a symbol of the unbreakable bond between climber and mountain.',
];

const faunaInspirations = [
  'The alpine wildlife of Northern Italy has inspired artists for centuries. This piece continues that tradition, capturing the grace and power of creatures that roam the forests and peaks of the Dolomites—a wearable reminder of nature\'s untamed beauty.',
  'For hunters and wildlife observers alike, this piece represents the deep connection between humans and the natural world. Each anatomical detail is studied from life, ensuring that the spirit of the animal is preserved in lasting bronze.',
  'In the valleys of the Euganean Hills, where Andrea Sampaoli\'s workshop stands, the sounds of nature provide a daily soundtrack. This piece channels that environment—the rustle of leaves, the call of a raptor, the stillness of a deer at dawn.',
  'The ancient hunting traditions of Europe carry a profound respect for wildlife. This piece embodies that respect, transforming the beauty of alpine fauna into wearable art that connects the wearer to centuries of natural heritage.',
  'Each species tells a story of adaptation, survival, and beauty. This bronze piece captures one such story, preserving in metal what nature sculpts in bone, feather, and fur—a timeless tribute to the wild.',
];

const equestrianInspirations = [
  'The bond between rider and horse is one of humanity\'s oldest partnerships. This piece celebrates that connection through iconic equestrian symbols, each one hand-cast in bronze to preserve the elegance and strength of the riding tradition.',
  'Whether your heart belongs to English dressage arenas or Western open ranges, this piece bridges both worlds. The precision of traditional tack and the freedom of the saddle come together in a design that speaks to every horse lover.',
  'Andrea Sampaoli draws from the rich equestrian heritage of the Italian countryside, where horsemanship is both art and tradition. This piece transforms everyday riding equipment into a refined bronze accessory.',
  'The rhythm of hooves, the creak of leather, the gleam of polished brass—this piece captures the sensory world of the stable and the arena, distilled into a bronze keepsake for riders and dreamers alike.',
  'From the first pony ride to the championship arena, the equestrian journey shapes a life. This handcrafted bronze piece honors that journey, carrying the spirit of horsemanship wherever you go.',
];

function getInspirationText(collection: Collection, handle: string): string {
  const hash = simpleHash(handle + '-inspiration');
  const pool =
    collection === 'mountain' ? mountainInspirations :
    collection === 'fauna' ? faunaInspirations :
    collection === 'equestrian' ? equestrianInspirations :
    mountainInspirations; // fallback
  return pool[hash % pool.length];
}

// ---------------------------------------------------------------------------
// Italian Versions
// ---------------------------------------------------------------------------

const mountainIntrosIT = [
  (title: string, cat: string) =>
    `Il ${title} è un ${cat} artigianale in bronzo che cattura lo spirito dell'alpinismo d'alta quota. Progettato per scalatori, alpinisti e amanti della montagna, questo pezzo trasforma l'attrezzatura da arrampicata in un'opera d'arte indossabile.`,
  (title: string, cat: string) =>
    `Ispirato alla bellezza delle Dolomiti italiane, il ${title} è un ${cat} in bronzo realizzato con cura dall'artigiano Andrea Sampaoli. Ogni dettaglio riflette la precisione e la passione dell'attrezzatura di montagna reale.`,
  (title: string, cat: string) =>
    `Per chi trova la libertà sulle pareti verticali e le creste ghiacciate, il ${title} in bronzo è un tributo in miniatura alle montagne. Ogni elemento è modellato su autentici attrezzi da arrampicata.`,
  (title: string, cat: string) =>
    `Il ${title} nasce dalle vette delle Alpi italiane. L'artigiano Andrea Sampaoli ricrea gli strumenti dell'alpinismo in miniatura squisita, forgiando ogni ${cat} a mano nel suo laboratorio nei Colli Euganei.`,
  (title: string, cat: string) =>
    `Per chi vive e respira la montagna, il ${title} è un ${cat} in bronzo che celebra la cultura dell'arrampicata. Ogni rilievo, zigrinatura e curva è fedelmente riprodotta dall'attrezzatura reale.`,
  (title: string, cat: string) =>
    `Porta la vetta con te. Il ${title} è un ${cat} artigianale in bronzo che trasforma l'attrezzatura alpinistica iconica in un elegante accessorio quotidiano.`,
  (title: string, cat: string) =>
    `Dai moschettoni alle piccozze, il ${title} in bronzo cattura la bellezza ingegneristica dell'attrezzatura di montagna. Fuso e rifinito a mano a Teolo, è costruito per durare come le montagne stesse.`,
  (title: string, cat: string) =>
    `Il ${title} è più di un ${cat} — è un tributo artigianale in bronzo al mondo verticale. Ispirato all'attrezzatura che tiene al sicuro gli scalatori, porta l'avventura alpina nella vita di tutti i giorni.`,
];

const faunaIntrosIT = [
  (title: string, cat: string) =>
    `Il ${title} è un ${cat} artigianale in bronzo che celebra la bellezza selvaggia della fauna alpina. Ogni dettaglio anatomico—corna, texture del pelo, pattern delle piume—è riprodotto fedelmente a mano nei Colli Euganei.`,
  (title: string, cat: string) =>
    `Tributo alla fauna incontaminata delle Alpi italiane, il ${title} in bronzo cattura l'essenza delle creature più maestose della natura. Ideale per cacciatori, naturalisti e appassionati di fauna selvatica.`,
  (title: string, cat: string) =>
    `Pensato per gli amanti della natura e della tradizione venatoria, il ${title} è un ${cat} in bronzo che porta lo spirito del bosco e della montagna nella vita quotidiana.`,
  (title: string, cat: string) =>
    `Il ${title} in bronzo è un capolavoro in miniatura di arte naturalistica. Ogni muscolo, piuma e punta di corno è scolpito dal vero, catturando grazia e potenza della fauna alpina.`,
  (title: string, cat: string) =>
    `Per chi custodisce il richiamo della natura, il ${title} è un ${cat} artigianale in bronzo che preserva la bellezza delle specie più iconiche d'Europa. Realizzato a mano nel laboratorio di Andrea Sampaoli.`,
  (title: string, cat: string) =>
    `Le creature più nobili della natura, immortalate nel bronzo. Il ${title} cattura lo spirito della fauna alpina con precisione anatomica e passione artigiana.`,
  (title: string, cat: string) =>
    `Il ${title} è un ${cat} in bronzo che racconta la storia del patrimonio selvatico europeo. Dai fondovalle alle creste, ogni dettaglio è fuso a mano per onorare gli animali del paesaggio alpino.`,
  (title: string, cat: string) =>
    `Creato per cacciatori e amanti della natura, il ${title} in bronzo è un'opera d'arte naturalistica indossabile. Ogni pezzo nasce da un modello scolpito a mano per garantire fedeltà anatomica.`,
];

const equestrianIntrosIT = [
  (title: string, cat: string) =>
    `Il ${title} è un ${cat} artigianale in bronzo ispirato al nobile mondo dell'equitazione. Dalla tradizione inglese allo spirito western, questo pezzo celebra il legame senza tempo tra cavaliere e cavallo.`,
  (title: string, cat: string) =>
    `Creato per gli appassionati di equitazione e gli amanti dei cavalli, il ${title} in bronzo riproduce fedelmente iconiche attrezzature equestri in miniatura.`,
  (title: string, cat: string) =>
    `Simbolo di eleganza e passione equestre, il ${title} è un ${cat} in bronzo che trasforma la bellezza della scuderia e dell'arena in arte indossabile.`,
  (title: string, cat: string) =>
    `Il ${title} in bronzo cattura il mondo raffinato dell'equitazione in miniatura. Dai filetti alle staffe, ogni curva e proporzione è modellata su autentiche attrezzature equestri.`,
  (title: string, cat: string) =>
    `Per cavalieri, addestratori e amanti dei cavalli, il ${title} è un ${cat} artigianale in bronzo che incarna lo spirito della scuderia. Ogni pezzo è fuso e lucidato individualmente.`,
  (title: string, cat: string) =>
    `In sella con stile. Il ${title} in bronzo trasforma gli strumenti dell'equitazione in elegante arte indossabile, forgiata nella tradizione italiana dall'artigiano Andrea Sampaoli.`,
  (title: string, cat: string) =>
    `Il ${title} è un ${cat} in bronzo che unisce il mondo del dressage inglese e della monta western. Ogni dettaglio è fedelmente riprodotto in bronzo artigianale.`,
  (title: string, cat: string) =>
    `Nato dall'amore per i cavalli e l'artigianato italiano, il ${title} in bronzo è un tributo allo stile di vita equestre. Ogni pezzo porta il calore del bronzo rifinito a mano.`,
];

const genericIntrosIT = [
  (title: string, cat: string) =>
    `Il ${title} è un ${cat} artigianale in bronzo creato dall'artigiano Andrea Sampaoli a Teolo, Italia. Combinando la lavorazione tradizionale italiana con il design contemporaneo, ogni pezzo racconta una storia.`,
  (title: string, cat: string) =>
    `Progettato e realizzato nel cuore dei Colli Euganei, il ${title} in bronzo mostra l'arte della fusione tradizionale italiana. Un accessorio unico per chi apprezza la qualità artigianale.`,
];

function getDescriptionIntroIT(title: string, collection: Collection, category: string, handle: string): string {
  const cat = categoryLabelsIT[category] || 'accessorio';
  const hash = simpleHash(handle);
  const pool =
    collection === 'mountain' ? mountainIntrosIT :
    collection === 'fauna' ? faunaIntrosIT :
    collection === 'equestrian' ? equestrianIntrosIT :
    genericIntrosIT;
  return pool[hash % pool.length](title, cat);
}

const mountainInspirationsIT = [
  'Nato dalla cultura dell\'arrampicata delle Dolomiti e delle Alpi occidentali, questo pezzo evoca l\'emozione di raggiungere una vetta all\'alba. Il bronzo richiama i toni caldi della roccia alpina sotto la luce dorata.',
  'Andrea Sampaoli creò il suo primo pezzo a tema montagna come regalo di Natale per i compagni di cordata nel 2003. Quello spirito di cameratismo e avventura condivisa vive in ogni dettaglio di questo design.',
  'Le montagne insegnano pazienza, precisione e rispetto per la natura. Questo pezzo incarna quei valori attraverso la sua lavorazione meticolosa.',
  'Dalla prima luce su una cresta dolomitica al trionfo silenzioso di una vetta raggiunta, questo pezzo distilla il paesaggio emotivo delle montagne in bronzo tangibile. Un cimelio per chi misura la vita in metri verticali.',
  'Ogni alpinista conosce la sensazione di affidare la propria vita all\'attrezzatura. Questo pezzo onora quella fiducia ricreando l\'equipaggiamento con la stessa precisione e integrità.',
];

const faunaInspirationsIT = [
  'La fauna alpina del Nord Italia ispira gli artisti da secoli. Questo pezzo continua quella tradizione, catturando la grazia e la potenza delle creature che abitano le foreste e le vette delle Dolomiti.',
  'Per cacciatori e osservatori della natura, questo pezzo rappresenta la connessione profonda tra l\'uomo e il mondo naturale. Ogni dettaglio anatomico è studiato dal vero.',
  'Nelle valli dei Colli Euganei, dove sorge il laboratorio di Andrea Sampaoli, i suoni della natura forniscono una colonna sonora quotidiana che si riflette in ogni creazione.',
  'Le antiche tradizioni venatorie europee portano un profondo rispetto per la fauna selvatica. Questo pezzo incarna quel rispetto, trasformando la bellezza della fauna alpina in arte indossabile.',
  'Ogni specie racconta una storia di adattamento, sopravvivenza e bellezza. Questo pezzo in bronzo cattura una di quelle storie, preservando nel metallo ciò che la natura scolpisce in ossa, piume e pelliccia.',
];

const equestrianInspirationsIT = [
  'Il legame tra cavaliere e cavallo è una delle più antiche partnership dell\'umanità. Questo pezzo celebra quella connessione attraverso simboli equestri iconici, ciascuno fuso a mano in bronzo.',
  'Che il vostro cuore appartenga ai maneggi di dressage inglese o alle distese del west, questo pezzo unisce entrambi i mondi.',
  'Andrea Sampaoli trae ispirazione dal ricco patrimonio equestre della campagna italiana, dove l\'arte del cavalcare è sia arte che tradizione.',
  'Il ritmo degli zoccoli, lo scricchiolio del cuoio, il luccichio dell\'ottone lucidato — questo pezzo cattura il mondo sensoriale della scuderia e dell\'arena, distillato in un ricordo di bronzo.',
  'Dalla prima lezione di equitazione al circuito dei campionati, il percorso equestre plasma una vita. Questo pezzo artigianale in bronzo onora quel viaggio.',
];

function getInspirationTextIT(collection: Collection, handle: string): string {
  const hash = simpleHash(handle + '-inspiration');
  const pool =
    collection === 'mountain' ? mountainInspirationsIT :
    collection === 'fauna' ? faunaInspirationsIT :
    collection === 'equestrian' ? equestrianInspirationsIT :
    mountainInspirationsIT;
  return pool[hash % pool.length];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface GeneratedSeo {
  collection: Collection;
  category: string;
  descriptionIntro: string;
  inspirationText: string;
  materials: string[];
  finishes: string[];
  sizes: string[];
  metaTitle: string;
  metaDescription: string;
  imageAlts: string[];
  hasShopifyDescription: boolean;
}

function getCollectionKeyword(collection: Collection, lang: 'en' | 'it'): string {
  if (lang === 'it') {
    switch (collection) {
      case 'mountain': return 'gioielli montagna';
      case 'fauna': return 'gioielli fauna selvatica';
      case 'equestrian': return 'gioielli equestri';
      default: return 'gioielli artigianali';
    }
  }
  switch (collection) {
    case 'mountain': return 'mountain jewelry';
    case 'fauna': return 'wildlife jewelry';
    case 'equestrian': return 'equestrian jewelry';
    default: return 'handcrafted jewelry';
  }
}

// Subject-specific keyword overrides for high-intent SEO terms.
// Matches against product title + tags; returns a tuned category label when applicable.
function getSubjectKeyword(title: string, tags: string[], category: string, lang: 'en' | 'it'): string | null {
  const haystack = (title + ' ' + tags.join(' ')).toLowerCase();
  const isNecklace = category === 'necklace';
  const isPin = category === 'pin';
  const isKeychain = category === 'keychain';

  // Mountain — carabiner / quickdraw
  if (/(moschettone|moschettoni|carabiner|quickdraw|rinvio|rinvii)/.test(haystack)) {
    if (lang === 'it') return isNecklace ? 'collana moschettone in bronzo' : 'moschettone in bronzo artigianale';
    return isNecklace ? 'handmade bronze carabiner necklace' : 'handmade bronze carabiner';
  }
  // Wildlife — wild boar
  if (/(cinghiale|boar)/.test(haystack)) {
    if (lang === 'it') return isPin ? 'spilla cinghiale in bronzo' : 'cinghiale in bronzo artigianale';
    return isPin ? 'handmade bronze wild boar pin' : 'handmade bronze wild boar';
  }
  // Wildlife — stag / deer
  if (/(cervo|capriolo|stag|deer)/.test(haystack)) {
    if (lang === 'it') return isPin ? 'spilla cervo in bronzo' : 'gioiello cervo in bronzo';
    return isNecklace ? 'handmade bronze stag pendant' : 'handmade bronze stag jewelry';
  }
  // Wildlife — ibex / chamois
  if (/(stambecco|camoscio|ibex|chamois)/.test(haystack)) {
    if (lang === 'it') return 'gioiello fauna alpina in bronzo';
    return 'handmade bronze alpine wildlife jewelry';
  }
  // Equestrian — horse
  if (/(cavallo|horse)/.test(haystack)) {
    if (lang === 'it') return isNecklace ? 'collana cavallo in bronzo' : 'gioiello equestre cavallo in bronzo';
    return isNecklace ? 'handmade bronze horse necklace' : 'handmade bronze horse jewelry';
  }
  // Equestrian — horseshoe
  if (/(ferro di cavallo|horseshoe)/.test(haystack)) {
    if (lang === 'it') return 'gioiello ferro di cavallo in bronzo';
    return 'handmade bronze horseshoe jewelry';
  }
  return null;
}

function getCategoryMetaLabel(category: string, collection: Collection, lang: 'en' | 'it'): string {
  if (lang === 'it') {
    const catIT = categoryLabelsIT[category] || 'accessorio';
    switch (collection) {
      case 'mountain': return `${catIT} montagna in bronzo`;
      case 'fauna': return `${catIT} fauna selvatica in bronzo`;
      case 'equestrian': return `${catIT} equestre in bronzo`;
      default: return `${catIT} artigianale in bronzo`;
    }
  }
  const catEN = categoryLabelsEN[category] || 'accessory';
  switch (collection) {
    case 'mountain': return `handmade bronze mountain ${catEN}`;
    case 'fauna': return `bronze wildlife ${catEN}`;
    case 'equestrian': return `bronze equestrian ${catEN}`;
    default: return `handcrafted bronze ${catEN}`;
  }
}

const imageViewLabels = ['front view', 'side view', 'detail', 'worn view', 'close-up', 'alternate angle', 'scale reference', 'packaging', 'lifestyle shot', 'color variant'];
const imageViewLabelsIT = ['vista frontale', 'vista laterale', 'dettaglio', 'indossato', 'primo piano', 'angolazione alternativa', 'riferimento scala', 'confezione', 'lifestyle', 'variante colore'];

export function generateProductSeo(product: ProductData, lang: 'en' | 'it' = 'en'): GeneratedSeo {
  const collection = detectCollection(product.tags);
  const category = detectProductCategory(product.productType, product.tags);
  const materials = extractMaterials(product.description, product.tags, product.options);
  const finishes = extractFinishes(product.options);
  const sizes = extractSizes(product.options);

  const hasShopifyDescription = product.description.length > 30;

  const descriptionIntro = lang === 'it'
    ? getDescriptionIntroIT(product.title, collection, category, product.handle)
    : getDescriptionIntro(product.title, collection, category, product.handle);

  const inspirationText = lang === 'it'
    ? getInspirationTextIT(collection, product.handle)
    : getInspirationText(collection, product.handle);

  // Meta title: max 60 chars — keep brand name complete.
  // Prefer a high-intent subject keyword (e.g. "carabiner necklace") over the generic category label.
  const subjectLabel = getSubjectKeyword(product.title, product.tags, category, lang);
  const catLabel = subjectLabel ?? getCategoryMetaLabel(category, collection, lang);
  const brand = 'Sampaoli Creazioni';
  const separator = ' | ';
  const reservedLen = separator.length + brand.length; // " | Sampaoli Creazioni" = 21
  const availableLen = 60 - reservedLen; // 39 chars for product + category hint

  let titleBody: string;
  const fullBody = `${product.title} – ${catLabel}`;
  if (fullBody.length <= availableLen) {
    titleBody = fullBody;
  } else if (catLabel.length <= availableLen) {
    // Lead with the keyword when the product title is too long to fit alongside it.
    titleBody = catLabel.charAt(0).toUpperCase() + catLabel.slice(1);
  } else {
    titleBody = product.title.length <= availableLen
      ? product.title
      : product.title.slice(0, availableLen - 1).replace(/\s+\S*$/, '') + '…';
  }
  const metaTitle = `${titleBody}${separator}${brand}`;

  // Meta description: 145-160 chars. No shipping claims (Shopify handles shipping).
  const kwChunk = getCollectionKeyword(collection, lang);
  const metaDescription = lang === 'it'
    ? `${product.title}: ${catLabel} fatto a mano in Italia. ${kwChunk} ispirati alla natura — pezzi unici fusi a mano nei Colli Euganei.`.slice(0, 160)
    : `${product.title}: ${catLabel}. Handmade in Italy, inspired by nature — unique ${kwChunk} hand-cast in our Euganean Hills workshop.`.slice(0, 160);

  // Image alts
  const viewLabels = lang === 'it' ? imageViewLabelsIT : imageViewLabels;
  const imageAlts = product.images.edges.map((_, idx) => {
    const view = viewLabels[idx] || viewLabels[idx % viewLabels.length];
    return lang === 'it'
      ? `${product.title} in bronzo – ${view}`
      : `${product.title} in bronze – ${view}`;
  });

  return {
    collection,
    category,
    descriptionIntro,
    inspirationText,
    materials,
    finishes,
    sizes,
    metaTitle,
    metaDescription,
    imageAlts,
    hasShopifyDescription,
  };
}
